<script lang="ts" setup>
import heic2any from 'heic2any';

const previews = defineModel<string[]>('previews', { default: [] });
const images = defineModel<File[]>('images', { default: [] });

async function convertHeicToPng(image: any) {
  let blobURL = URL.createObjectURL(image);
  let blobRes = await fetch(blobURL);
  let blob = await blobRes.blob();
  let conversionResult = await heic2any({ blob });

  return URL.createObjectURL(conversionResult as Blob);
}

async function processImage(image: any) {
  if (image.type === 'image/heic') {
    return await convertHeicToPng(image);
  }

  return URL.createObjectURL(image);
}

const isShowFileError = computed(() => !!fileError.value);

const isImageLoading = ref(false);
const fileError = ref('');

async function addImage(image: File) {
  const isCorrectSize = image.size <= 10485760; // 10Мб в байтах

  if (isCorrectSize) {
    isImageLoading.value = true;
    const imageUrl = await processImage(image);
    isImageLoading.value = false;

    previews.value.unshift(imageUrl);
    images.value.unshift(image);
    fileError.value = '';
  } else {
    fileError.value = 'Максимальный размер изображения - 10Мб';
  }
}

function removeImage(index: number) {
  previews.value.splice(index, 1);
  images.value.splice(index, 1);
}
</script>

<template>
  <div class="images-upload-block__section">
    <p class="images-upload-block__section-header">
      {{ previews.length ? `Загружено: ${previews.length}, можно ещё ${10 - previews.length}` : 'Добавьте фотографии' }}
    </p>

    <div class="images-upload-block__images">
      <FileInput
        v-if="previews.length < 10"
        :is-loading="isImageLoading"
        @add-image="addImage"
      >
        <div class="images-upload-block__image-box">
          <SvgoSpinner
            v-if="isImageLoading"
            class="animate-spin text-3xl mt-1"
          />

          <SvgoPlusIcon
            v-else
            class="text-3xl mt-1"
          />
        </div>
      </FileInput>

      <div
        v-for="(preview, key) in previews"
        :key="`${key}-${preview}`"
        class="images-upload-block__image-box"
      >
        <img
          :src="preview"
          alt="product review image"
          class="images-upload-block__image"
        />

        <div
          class="images-upload-block__image-remove"
          @click="removeImage(key)"
        >
          <SvgoCross />
        </div>
      </div>
    </div>

    <FoldableBlock v-model="isShowFileError">
      <div class="text-xs py-1 px-2">
        <span
          v-if="fileError"
          class="text-red"
        >
          {{ fileError }}
        </span>
      </div>
    </FoldableBlock>
  </div>
</template>

<style lang="scss" scoped>
.images-upload-block {
  &__section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    width: 100%;

    &-header {
      font-weight: $bold;
      font-size: $text-sm;
    }

    &-error {
      color: $red;
      font-size: $text-sm;
    }
  }

  &__images {
    display: flex;
    gap: 8px;
    overflow: auto;
    width: 100%;
  }

  &__image-box {
    position: relative;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    object-fit: cover;
    overflow: hidden;
    width: 64px;
    height: 64px;
    border-radius: 12px;
    background-color: $black-10;
    transition: opacity 0.3s;

    &:hover {
      opacity: 0.7;
    }
  }

  &__image-remove {
    position: absolute;
    top: 3px;
    right: 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 14px;
    height: 14px;
    padding: 2px 1px 0;
    border-radius: 100%;
    background-color: $white;
    cursor: pointer;
    transition: opacity 0.3s;

    &:hover {
      opacity: 0.7;
    }
  }
}
</style>
